import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background: linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4, #ffeaa7);
  background-size: 400% 400%;
  animation: gradientShift 8s ease infinite;
  position: relative;
  
  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="20" cy="20" r="2" fill="%23ffffff" opacity="0.3"/><circle cx="80" cy="40" r="1.5" fill="%23ffffff" opacity="0.4"/><circle cx="40" cy="80" r="1" fill="%23ffffff" opacity="0.5"/><circle cx="90" cy="90" r="2.5" fill="%23ffffff" opacity="0.2"/><circle cx="10" cy="60" r="1.8" fill="%23ffffff" opacity="0.3"/></svg>') repeat;
    animation: float 20s ease-in-out infinite;
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }
`;

const MainContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 1200px;
  gap: 40px;
  z-index: 1;
  position: relative;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const LeftPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: #000000;
  padding: 20px;
  order: 2;
  
  @media (max-width: 768px) {
    order: 2;
  }
`;

const BrandTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 900;
  color: #000000;
  margin-bottom: 20px;
  text-transform: uppercase;
  letter-spacing: 3px;
  text-shadow: 6px 6px 0px #ffffff;
  font-family: 'Arial Black', Arial, sans-serif;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const BrandSubtitle = styled.p`
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 30px;
  background: #ffeb3b;
  padding: 15px 25px;
  border: 4px solid #000000;
  transform: rotate(-2deg);
  box-shadow: 6px 6px 0px #000000;
  max-width: 400px;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 30px;
  max-width: 400px;
`;

const FeatureItem = styled.div`
  background: #ffffff;
  border: 3px solid #000000;
  padding: 12px 20px;
  font-weight: 700;
  transform: rotate(1deg);
  box-shadow: 4px 4px 0px #000000;
  transition: all 0.3s ease;
  
  &:hover {
    transform: rotate(-1deg) translate(-2px, -2px);
    box-shadow: 6px 6px 0px #000000;
  }
  
  &:nth-child(even) {
    transform: rotate(-1deg);
    background: #4ecdc4;
  }
  
  &:nth-child(3n) {
    background: #96ceb4;
  }
`;

const QRSection = styled.div`
  background: #ffffff;
  border: 4px solid #000000;
  padding: 20px;
  text-align: center;
  box-shadow: 6px 6px 0px #000000;
  transform: rotate(2deg);
  max-width: 300px;
  
  &:hover {
    transform: rotate(0deg);
  }
`;

const QRTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 900;
  margin-bottom: 15px;
  color: #000000;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const QRImage = styled.img`
  width: 150px;
  height: 150px;
  border: 3px solid #000000;
  margin-bottom: 10px;
`;

const QRText = styled.p`
  font-size: 0.9rem;
  font-weight: 700;
  color: #000000;
  line-height: 1.4;
`;

const Card = styled.div`
  background: #ffffff;
  border: 6px solid #000000;
  border-radius: 0;
  padding: 40px;
  width: 100%;
  max-width: 400px;
  box-shadow: 12px 12px 0px #000000;
  transform: rotate(-1deg);
  transition: all 0.3s ease;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  order: 1;
  
  &:hover {
    transform: rotate(0deg) scale(1.02);
    box-shadow: 16px 16px 0px #000000;
  }
  
  @media (max-width: 768px) {
    order: 1;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 900;
  color: #000000;
  text-align: center;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: 4px 4px 0px #ff6b6b;
  font-family: 'Arial Black', Arial, sans-serif;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #000000;
  text-align: center;
  margin-bottom: 30px;
  line-height: 1.4;
  font-weight: 700;
  background: #ffeb3b;
  padding: 8px 16px;
  border: 3px solid #000000;
  transform: rotate(1deg);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Input = styled.input`
  padding: 15px;
  border: 4px solid #000000;
  border-radius: 0;
  background: #ffffff;
  color: #000000;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 4px 4px 0px #4ecdc4;
  
  &::placeholder {
    color: #666666;
    font-weight: 600;
  }
  
  &:focus {
    outline: none;
    border-color: #ff6b6b;
    background: #f8f8f8;
    box-shadow: 6px 6px 0px #ff6b6b;
    transform: translate(-2px, -2px);
  }
`;

const Button = styled.button`
  padding: 18px;
  background: #45b7d1;
  border: 4px solid #000000;
  border-radius: 0;
  color: #000000;
  font-size: 18px;
  font-weight: 900;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 6px 6px 0px #000000;
  font-family: 'Arial Black', Arial, sans-serif;
  
  &:hover {
    transform: translate(-2px, -2px);
    box-shadow: 8px 8px 0px #000000;
    background: #96ceb4;
  }
  
  &:active {
    transform: translate(2px, 2px);
    box-shadow: 2px 2px 0px #000000;
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    background: #cccccc;
  }
`;

const LinkText = styled.div`
  text-align: center;
  margin-top: 20px;
  color: #000000;
  font-weight: 700;
  
  a {
    color: #ff6b6b;
    text-decoration: none;
    font-weight: 900;
    background: #ffeb3b;
    padding: 2px 6px;
    border: 2px solid #000000;
    
    &:hover {
      background: #4ecdc4;
      color: #000000;
    }
  }
`;

const ErrorMessage = styled.div`
  background: #ff6b6b;
  border: 4px solid #000000;
  border-radius: 0;
  padding: 15px;
  color: #000000;
  text-align: center;
  margin-bottom: 20px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 4px 4px 0px #000000;
  transform: rotate(-1deg);
`;

function Login() {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/api/login', formData);
      
      if (response.data.success) {
        // 使用AuthContext的login方法更新全局状态
        login(response.data.user, response.data.token);
        
        // 跳转到首页
        navigate('/');
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      console.error('登录失败:', error);
      setError(error.response?.data?.message || '登录失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <MainContainer>
        <LeftPanel>
          <BrandTitle>🎨 AI画图神器：midjourney</BrandTitle>
          <BrandSubtitle>知识星球专属 · 创意无限</BrandSubtitle>
          
          <FeatureList>
            <FeatureItem>🚀 一键生成精美图片</FeatureItem>
            <FeatureItem>🎯 免费无线体验</FeatureItem>
            <FeatureItem>⚡ 秒级响应超快体验</FeatureItem>
            <FeatureItem>🔥 知识星球会员专享</FeatureItem>
            <FeatureItem>💎 高质量AI绘画引擎</FeatureItem>
          </FeatureList>
          
          <QRSection>
            <QRTitle>🌟 关注公众号</QRTitle>
            <QRImage 
              src="https://j.163264.com/zlbigger.com/2024/12/qrcode_for_gh_49fff015f5f1_258.jpg" 
              alt="公众号二维码"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div style={{display: 'none', padding: '20px', background: '#f0f0f0', border: '2px dashed #ccc'}}>
              二维码加载失败
            </div>
            <QRText>
              扫码关注获取更多AI工具<br/>
              <strong>微信号：zlbigger</strong>
            </QRText>
          </QRSection>
        </LeftPanel>
        
        <Card>
          <Title>👑 会员登录</Title>
          <Subtitle>欢迎回到创作空间</Subtitle>
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          <Form onSubmit={handleSubmit}>
            <Input
              type="text"
              name="username"
              placeholder="🔑 输入用户名"
              value={formData.username}
              onChange={handleChange}
              required
            />
            
            <Input
              type="password"
              name="password"
              placeholder="🔒 输入密码"
              value={formData.password}
              onChange={handleChange}
              required
            />
            
            <Button type="submit" disabled={loading}>
              {loading ? '🔍 验证中...' : '🚀 开始创作'}
            </Button>
          </Form>
          
          <LinkText>
            还没有账户？ <Link to="/register">立即注册</Link><br/>
            <small style={{display: 'block', lineHeight: '1.6', background: '#f8f9fa', padding: '12px', border: '2px solid #000', marginTop: '20px'}}>
              <strong>🌟 加入知识星球获取账号</strong><br/>
              星球链接：<a href="https://t.zsxq.com/zQKmU" target="_blank" rel="noopener noreferrer" style={{color: '#ff6b6b', fontWeight: 'bold'}}>https://t.zsxq.com/zQKmU</a><br/>
              微信咨询：<strong>zlbigger</strong><br/>
              💡 星球会员专享AI绘画工具
            </small>
          </LinkText>
        </Card>
      </MainContainer>
    </Container>
  );
}

export default Login;